/**
 * FASE 5-7: Frontend - Timeout, Invalidação de Cache, Prevenção de Duplo Clique
 * Conforme PLANO_CORRECAO_DEFINITIVA.md
 * 
 * Mudanças:
 * 5. Timeout + Fallback no frontend
 * 6. Invalidação de cache React Query
 * 7. Prevenção de duplo clique (desabilitar botão)
 */

import { toast } from "sonner";

// ============================================================================
// FASE 7: PREVENÇÃO DE DUPLO CLIQUE
// ============================================================================

let editingInProgress = false;

export function isEditingInProgress(): boolean {
  return editingInProgress;
}

export function setEditingInProgress(value: boolean): void {
  editingInProgress = value;
  console.log(`[setEditingInProgress] Editando: ${value}`);
}

// CORRECAO: Limpar lock ao cancelar
export function clearEditingLock(): void {
  editingInProgress = false;
  console.log(`[clearEditingLock] Lock liberado`);
}

// ============================================================================
// FASE 5: TIMEOUT + FALLBACK (Frontend)
// ============================================================================

export async function withTimeoutAndFallback<T>(
  promise: Promise<T>,
  timeoutMs: number = 5000,
  fallbackValue?: T
): Promise<T> {
  const startTime = Date.now();
  
  // LOG 1: Iniciando com timeout
  console.log(`[withTimeoutAndFallback] INÍCIO - timeout=${timeoutMs}ms`);

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      const elapsed = Date.now() - startTime;
      reject(new Error(`[withTimeoutAndFallback] Timeout após ${elapsed}ms`));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    const elapsed = Date.now() - startTime;
    // LOG 2: Sucesso
    console.log(`[withTimeoutAndFallback] FIM (sucesso) - tempo=${elapsed}ms`);
    return result;
  } catch (error) {
    const elapsed = Date.now() - startTime;
    // LOG 3: Erro/Timeout
    console.error(`[withTimeoutAndFallback] ERRO após ${elapsed}ms:`, error);

    // LOG 4: Usando fallback
    if (fallbackValue !== undefined) {
      console.warn(`[withTimeoutAndFallback] Usando fallback`);
      return fallbackValue;
    }

    throw error;
  }
}

// ============================================================================
// FASE 6: INVALIDAÇÃO DE CACHE REACT QUERY
// ============================================================================

export function invalidateClassificationMatrixCache(utils: any): void {
  // LOG 5: Invalidando cache
  console.log(`[invalidateClassificationMatrixCache] Invalidando cache...`);

  try {
    // Invalidar todas as queries relacionadas à Matriz de Classificação
    utils.ergos?.classificationMatrix?.getCurrentRiskMatrix?.invalidate?.();
    utils.ergos?.classificationMatrix?.getPublishedQualifiers?.invalidate?.();
    utils.ergos?.classificationMatrix?.ensureClassificationDraft?.invalidate?.();
    
    // LOG 6: Cache invalidado
    console.log(`[invalidateClassificationMatrixCache] Cache invalidado com sucesso`);
  } catch (error) {
    console.error(`[invalidateClassificationMatrixCache] Erro ao invalidar cache:`, error);
  }
}

// ============================================================================
// FASE 5-7: HANDLER COMPLETO PARA "EDITAR MATRIZ"
// ============================================================================

export async function handleEditMatrixWithSafeguards(
  ensureDraftMutation: any,
  utils: any,
  callbacks: {
    onSuccess: (result: any) => void;
    onError: (error: any) => void;
    onFinally: () => void;
  }
): Promise<void> {
  // FASE 7: Verificar se já está editando
  if (isEditingInProgress()) {
    console.warn(`[handleEditMatrixWithSafeguards] Já existe edição em andamento. Ignorando duplo clique.`);
    toast.warning("Edição já em andamento. Aguarde...");
    return;
  }

  // FASE 7: Marcar como editando
  setEditingInProgress(true);
  
  // LOG 7: Iniciando
  console.log(`[handleEditMatrixWithSafeguards] INÍCIO`);
  const startTime = Date.now();

  try {
    // LOG 8: Invalidando cache antes de carregar
    console.log(`[handleEditMatrixWithSafeguards] Invalidando cache...`);
    invalidateClassificationMatrixCache(utils);

    // FASE 5: Executar com timeout (15s)
    // LOG 9: Aguardando mutação com timeout
    console.log(`[handleEditMatrixWithSafeguards] Aguardando mutação com timeout...`);
    
    const result = await withTimeoutAndFallback(
      ensureDraftMutation.mutateAsync(),
      15000 // CORRECAO: Aumentado de 5s para 15s (sincronização de severidades + células)
    );

    const elapsed = Date.now() - startTime;
    // LOG 10: Sucesso
    console.log(`[handleEditMatrixWithSafeguards] FIM (sucesso) - tempo=${elapsed}ms`);

    // Chamar callback de sucesso
    callbacks.onSuccess(result);

    // LOG 11: Invalidando cache após sucesso
    console.log(`[handleEditMatrixWithSafeguards] Invalidando cache após sucesso...`);
    invalidateClassificationMatrixCache(utils);

  } catch (error: any) {
    const elapsed = Date.now() - startTime;
    // LOG 12: Erro
    console.error(`[handleEditMatrixWithSafeguards] ERRO após ${elapsed}ms:`, error);

    // Exibir mensagem de erro clara
    const errorMessage = error?.message || "Erro ao iniciar edição";
    toast.error(errorMessage);

    // Chamar callback de erro
    callbacks.onError(error);

  } finally {
    // FASE 7: Desmarcar como editando
    setEditingInProgress(false);
    
    // LOG 13: Finalizando
    console.log(`[handleEditMatrixWithSafeguards] FINALIZANDO`);

    // Chamar callback de finalização
    callbacks.onFinally();
  }
}

// ============================================================================
// CRITÉRIOS DE ACEITE - Helpers para testes
// ============================================================================

export function validateEditMatrixFlow(result: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!result) {
    errors.push("Resultado é nulo");
  } else {
    if (!result.draftVersionId) errors.push("draftVersionId ausente");
    if (!result.version) errors.push("version ausente");
    if (!Array.isArray(result.probabilities)) errors.push("probabilities não é array");
    if (!Array.isArray(result.severities)) errors.push("severities não é array");
    if (!Array.isArray(result.cells)) errors.push("cells não é array");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export function getEditMatrixMetrics(): {
  isEditingInProgress: boolean;
  timestamp: number;
} {
  return {
    isEditingInProgress: editingInProgress,
    timestamp: Date.now(),
  };
}
